import React, { useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { FaImage, FaVideo, FaFile } from "react-icons/fa"
import { toast } from "react-toastify"
import { api } from "../../app/Api/ApiSlice"

const CreateOrgPost = () => {
    const navigate = useNavigate()
    const { organization } = useParams()

    const [postText, setPostText] = useState("")
    const [audience, setAudience] = useState("public")
    const [postLabel, setPostLabel] = useState("Post")
    const [files, setFiles] = useState([])
    const [mediaType, setMediaType] = useState(null) // image | video | document
    const [isLoading, setIsLoading] = useState(false)

    const imageRef = useRef(null)
    const videoRef = useRef(null)
    const docRef = useRef(null)

    const triggerInput = (ref) => ref.current?.click()

    const addFiles = (e, type) => {
        const selectedFiles = Array.from(e.target.files)

        if (mediaType && mediaType !== type) {
            toast.error("Only one media type per post is allowed")
            return
        }

        setMediaType(type)
        setFiles((prev) => [...prev, ...selectedFiles])
        e.target.value = null
    }

    const removeFile = (index) => {
        const updated = files.filter((_, i) => i !== index)
        setFiles(updated)

        if (updated.length === 0) {
            setMediaType(null)
        }
    }

    const uploadToS3 = async (file) => {
        const presignRes = await api.post("/uploads/presign", {
            filename: file.name,
            contentType: file.type,
        })

        const { uploadUrl, key } = presignRes.data

        const res = await fetch(uploadUrl, {
            method: "PUT",
            headers: {
                "Content-Type": file.type,
            },
            body: file,
        })

        if (!res.ok) {
            throw new Error(`Upload failed: ${file.name}`)
        }

        const baseType = file.type.split("/")[0]

        return {
            key,
            type:
                baseType === "image"
                    ? "image"
                    : baseType === "video"
                        ? "video"
                        : "document",
            mimeType: file.type,
            name: file.name,
            size: file.size,
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        if (!postText && files.length === 0) {
            toast.error("Post cannot be empty")
            return
        }

        try {
            setIsLoading(true)

            const uploadedFiles = await Promise.all(
                files.map((file) => uploadToS3(file))
            )
            await api.post(`/${organization}/posts`, {
                postText,
                postLabel,
                audience,
                files: uploadedFiles,
            })

            toast.success("Post created successfully")
            navigate(-1)
        } catch (err) {
            toast.error(err.message || "Post creation failed")
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className="">
            <div className="form-wrapper create-form">
                <h3 className="form-title">Create a new post</h3>

                <form onSubmit={handleSubmit}>
                    <div className="post-type-select">
                        <select name="type" id="" onChange={(e) => setPostLabel(e.target.value)}>
                            <option value="post">Post</option>
                            <option value="event">Event</option>
                            <option value="project">project</option>
                            <option value="information">Information</option>
                            <option value="important">Important</option>
                            <option value="notice">Notice</option>
                            <option value="proposal">Proposal</option>
                            <option value="reminder">Reminder</option>
                            <option value="announcement">Announcement</option>
                            <option value="urgent">Urgent</option>
                            <option value="warning">Warning</option>
                            <option value="request">Request</option>
                            <option value="update">Update</option>
                            <option value="correction">Correction</option>
                        </select>
                    </div>
                    <textarea
                        type="text"
                        className="post-text"
                        placeholder="Type something..."
                        value={postText}
                        onChange={(e) => setPostText(e.target.value)}
                        rows={10}
                    />

                    {/* Media actions */}
                    <div className="select-file-buttons">
                        <button type="button" onClick={() => triggerInput(imageRef)}>
                            <FaImage />
                        </button>
                        <button type="button" onClick={() => triggerInput(videoRef)}>
                            <FaVideo />
                        </button>
                        <button type="button" onClick={() => triggerInput(docRef)}>
                            <FaFile />
                        </button>
                    </div>

                    {/* Hidden inputs */}
                    <input
                        ref={imageRef}
                        type="file"
                        hidden
                        accept="image/*"
                        multiple
                        onChange={(e) => addFiles(e, "image")}
                    />
                    <input
                        ref={videoRef}
                        type="file"
                        hidden
                        accept="video/*"
                        multiple
                        onChange={(e) => addFiles(e, "video")}
                    />
                    <input
                        ref={docRef}
                        type="file"
                        hidden
                        accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt"
                        multiple
                        onChange={(e) => addFiles(e, "document")}
                    />

                    {/* Previews */}
                    <div className="file-previews">
                        {mediaType === "image" &&
                            files.map((file, i) => (
                                <div key={i} className="form-image">
                                    <img
                                        src={URL.createObjectURL(file)}
                                        alt="preview"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeFile(i)}
                                    >
                                        Remove
                                    </button>
                                </div>
                            ))}

                        {mediaType === "video" &&
                            files.map((file, i) => (
                                <div key={i}>
                                    <video
                                        controls
                                        width="260"
                                        src={URL.createObjectURL(file)}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeFile(i)}
                                    >
                                        Remove
                                    </button>
                                </div>
                            ))}

                        {mediaType === "document" &&
                            files.map((file, i) => (
                                <div key={i}>
                                    <p>{file.name}</p>
                                    <button
                                        type="button"
                                        onClick={() => removeFile(i)}
                                    >
                                        Remove
                                    </button>
                                </div>
                            ))}
                    </div>

                    {/* Audience */}
                    <div className="audience form-select">
                        <label>Audience</label>
                        <select
                            value={audience}
                            onChange={(e) => setAudience(e.target.value)}
                        >
                            <option value="members">Members</option>
                            <option value="friends">Friends</option>
                            <option value="public">Public</option>
                        </select>
                    </div>

                    <div className="post-form-btn">
                        <button type="submit" disabled={isLoading}>
                            {isLoading ? "Publishing..." : "Publish"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default CreateOrgPost
